import { useId } from 'react';
import formStyles from '~/styles/form.module.css';
import cnh from '~/helpers/cnh';
import useTranslation from '~/hooks/useTranslation';
import FormLabel from './FormLabel';

type Option = {
  label: string;
  value: string | number;
};

type Props = {
  label: string;
  value: string | number;
  options?: Option[];
  optionGroups?: {
    label: string;
    options: Option[];
  }[];
  required?: boolean;
  visuallyHiddenLabel?: boolean;
  onChange: (event: React.ChangeEvent<HTMLSelectElement>) => void;
  allowEmpty?: boolean;
};

export default function PlainSelectField({
  label,
  value,
  options,
  optionGroups,
  required = false,
  visuallyHiddenLabel = false,
  allowEmpty = false,
  onChange,
}: Props) {
  const fieldId = useId();
  const translate = useTranslation();

  if (!options && !optionGroups) {
    return null;
  }

  return (
    <div>
      <FormLabel
        htmlFor={fieldId}
        visuallyHidden={visuallyHiddenLabel}
        required={required}
      >
        {label}
      </FormLabel>

      <select
        id={fieldId}
        className={cnh(formStyles.formControl, formStyles.formSelect)}
        value={value}
        onChange={onChange}
        required={required}
      >
        {allowEmpty && (
          <option value="">{translate('form.select_empty')}</option>
        )}

        {options && (
          <>
            {options.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </>
        )}

        {optionGroups && (
          <>
            {optionGroups.map((optionGroup) => (
              <optgroup
                key={optionGroup.label}
                label={optionGroup.label}
              >
                {optionGroup.options.map((option, optionIndex) => (
                  <option
                    // eslint-disable-next-line react/no-array-index-key
                    key={`${option.value}_${optionIndex}`}
                    value={option.value}
                  >
                    {option.label}
                  </option>
                ))}
              </optgroup>
            ))}
          </>
        )}
      </select>
    </div>
  );
}
